import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X, FloppyDisk, CalendarBlank, Truck, IdentificationCard,
  ClipboardText, Plus,
} from "@phosphor-icons/react";
import { DateTimePicker } from "@/components/DateTimePicker";

const ACCENT = "#10B981";

export default function EditRecoleccionModal({ rec, onClose, onSaved }) {
  const [providers, setProviders] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    when: rec.appointment_datetime ? new Date(rec.appointment_datetime) : null,
    provider_id: rec.provider_id || "",
    unit_type_id: rec.unit_type_id || "",
    persona_recolecta: rec.persona_recolecta || "",
    placas: rec.placas || "",
    pedidos: rec.pedidos && rec.pedidos.length > 0 ? [...rec.pedidos] : [""],
    notes: rec.notes || "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([
          api.get("/catalogs/providers"),
          api.get("/catalogs/unit-types"),
        ]);
        setProviders(p.data);
        setUnitTypes(u.data);
      } catch (err) {
        console.debug("[EditRecModal] load catalogs failed", err);
      }
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPedido = (i, v) =>
    setForm((f) => ({ ...f, pedidos: f.pedidos.map((p, idx) => (idx === i ? v : p)) }));
  const addPedido = () => setForm((f) => ({ ...f, pedidos: [...f.pedidos, ""] }));
  const removePedido = (i) =>
    setForm((f) => ({
      ...f,
      pedidos: f.pedidos.length === 1 ? [""] : f.pedidos.filter((_, idx) => idx !== i),
    }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.when || !form.provider_id || !form.unit_type_id) {
      toast.error("Fecha, cliente y tipo de unidad son obligatorios");
      return;
    }
    if (!form.persona_recolecta.trim() || !form.placas.trim()) {
      toast.error("Captura persona y placas");
      return;
    }
    const pedidosClean = form.pedidos.map((p) => p.trim()).filter(Boolean);
    if (pedidosClean.length === 0) {
      toast.error("Agrega al menos un pedido o RMA");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/recolecciones/${rec.id}`, {
        appointment_datetime: form.when.toISOString(),
        provider_id: form.provider_id,
        unit_type_id: form.unit_type_id,
        persona_recolecta: form.persona_recolecta,
        placas: form.placas,
        pedidos: pedidosClean,
        notes: form.notes,
      });
      toast.success(`Recolección ${data.folio} actualizada`);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="edit-rec-modal"
    >
      <div className="bg-[#111111] border border-white/15 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-sm flex items-center justify-center border"
            style={{ backgroundColor: "rgba(16,185,129,0.15)", borderColor: "rgba(16,185,129,0.4)" }}
          >
            <Truck size={16} weight="fill" style={{ color: ACCENT }} />
          </div>
          <div className="flex-1">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-white/50">
              Editar Recolección
            </div>
            <div className="font-mono-data font-bold text-lg" style={{ color: ACCENT }}>
              {rec.folio}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-sm text-white/50 hover:bg-white/5 hover:text-white"
            data-testid="edit-rec-close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-5">
          <Field label={<><CalendarBlank size={12} className="inline mr-1" /> Fecha y hora</>}>
            <DateTimePicker
              value={form.when}
              onChange={(d) => update("when", d)}
              testId="edit-rec-when"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={<><Truck size={12} className="inline mr-1" /> Cliente / Proveedor</>}>
              <select
                data-testid="edit-rec-cliente"
                value={form.provider_id}
                onChange={(e) => update("provider_id", e.target.value)}
                className="input-dark"
              >
                <option value="">Selecciona…</option>
                {providers.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
            </Field>
            <Field label="Tipo de Unidad">
              <select
                data-testid="edit-rec-unit"
                value={form.unit_type_id}
                onChange={(e) => update("unit_type_id", e.target.value)}
                className="input-dark"
              >
                <option value="">Selecciona…</option>
                {unitTypes.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={<><IdentificationCard size={12} className="inline mr-1" /> Persona que recolecta</>}>
              <input
                data-testid="edit-rec-persona"
                value={form.persona_recolecta}
                onChange={(e) => update("persona_recolecta", e.target.value)}
                className="input-dark"
                placeholder="Nombre del chofer / recolector"
              />
            </Field>
            <Field label="Placas de la unidad">
              <input
                data-testid="edit-rec-placas"
                value={form.placas}
                onChange={(e) => update("placas", e.target.value.toUpperCase())}
                className="input-dark font-mono"
                placeholder="ABC-123"
              />
            </Field>
          </div>

          <Field label={<><ClipboardText size={12} className="inline mr-1" /> Pedidos / RMA · {form.pedidos.filter((p) => p.trim()).length}</>}>
            <div className="space-y-2">
              {form.pedidos.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`edit-rec-pedido-${i}`}
                    value={p}
                    onChange={(e) => setPedido(i, e.target.value)}
                    placeholder={`Pedido / RMA #${i + 1}`}
                    className="input-dark flex-1 font-mono"
                  />
                  <button
                    type="button"
                    data-testid={`edit-rec-remove-pedido-${i}`}
                    onClick={() => removePedido(i)}
                    className="px-3 rounded-sm border border-white/10 text-white/50 hover:bg-white/5"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="edit-rec-add-pedido"
                onClick={addPedido}
                className="flex items-center gap-2 text-xs font-mono-data uppercase tracking-widest hover:brightness-110"
                style={{ color: ACCENT }}
              >
                <Plus size={14} /> Agregar pedido
              </button>
            </div>
          </Field>

          <Field label="Notas">
            <textarea
              rows={3}
              data-testid="edit-rec-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Instrucciones especiales…"
              className="input-dark resize-y"
            />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-sm border border-white/10 text-sm text-white/70 hover:bg-white/5"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-rec-save"
              className="flex items-center gap-2 px-5 py-2 rounded-sm text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] disabled:opacity-50 transition-transform"
              style={{ backgroundColor: ACCENT }}
            >
              <FloppyDisk size={15} weight="fill" /> {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-dark {
          width: 100%;
          padding: 0.55rem 0.7rem;
          border-radius: 2px;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          font-size: 0.85rem;
          outline: none;
        }
        .input-dark:focus { border-color: ${ACCENT}; box-shadow: 0 0 0 1px ${ACCENT}; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
